import React, { useState, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import {
  FaChevronRight,
  FaChevronDown,
  FaPlus,
  FaTrash,
  FaPencilAlt,
  FaFolder,
} from "react-icons/fa";
import { VscFolder, VscFolderOpened, VscFile } from "react-icons/vsc";
import ContextMenu from "../common/ContextMenu";
import { personalTemplateService } from "../../services/personalTemplateService";
import { sharedTemplateService } from "../../services/sharedTemplateService";
import { useConfirmation } from "../common/ConfirmationContext";
import { useToast } from "../common/ToastContext";

const TemplateNode = ({
  node,
  depth,
  onContextMenu,
  initiallyExpanded,
  selectedPath,
  onSelect,
  onAddFile,
  onAddFolder,
  isAddingFile,
  isAddingFolder,
  onCancelAdd,
  isRenamingFile,
  onRenameFile,
  onCancelRename,
  existingPaths,
  readOnly,
}) => {
  const [isOpen, setIsOpen] = useState(initiallyExpanded);
  const [newName, setNewName] = useState("");
  const newInputRef = useRef(null);
  const isFolder = node.type === "folder";
  const isSelected = selectedPath === node.path;

  useEffect(() => {
    if (selectedPath && selectedPath.startsWith(node.path)) {
      setIsOpen(true);
    }
  }, [selectedPath, node.path]);

  useEffect(() => {
    if (
      (isAddingFile || isAddingFolder || isRenamingFile) &&
      newInputRef.current
    ) {
      newInputRef.current.focus();
      if (isRenamingFile) {
        newInputRef.current.select();
      }
    }
  }, [isAddingFile, isAddingFolder, isRenamingFile]);

  useEffect(() => {
    if (isRenamingFile === node.path) {
      setNewName(node.name);
    }
  }, [isRenamingFile, node.name, node.path]);

  const toggleOpen = () => setIsOpen(!isOpen);

  const handleContextMenu = (e) => {
    e.preventDefault();
    if (!readOnly) {
      onContextMenu(e, node);
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (isFolder) {
      toggleOpen();
    } else {
      onSelect(node.path);
    }
  };

  const handleNewItemKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmedName = newName.trim();
      if (
        trimmedName &&
        !existingPaths.includes(`${node.path}/${trimmedName}`)
      ) {
        if (isAddingFile) {
          onAddFile(node.path, trimmedName);
        } else if (isAddingFolder) {
          onAddFolder(node.path, trimmedName);
        } else if (isRenamingFile) {
          onRenameFile(node.path, trimmedName);
        }
        setNewName("");
      }
    } else if (e.key === "Escape") {
      onCancelAdd();
      onCancelRename();
      setNewName("");
    }
  };

  const handleNewItemBlur = () => {
    onCancelAdd();
    onCancelRename();
    setNewName("");
  };

  const handleNewItemChange = (e) => {
    const value = e.target.value;
    if (/^[a-zA-Z0-9_-]*$/.test(value)) {
      setNewName(value);
    }
  };

  return (
    <div>
      {isRenamingFile === node.path && !readOnly ? (
        <div
          className="flex items-center py-1 px-2"
          style={{ paddingLeft: `${depth * 12}px` }}
        >
          <VscFile className="text-gray-400 w-4 h-4 mr-2" />
          <input
            ref={newInputRef}
            type="text"
            className="bg-gray-800 text-gray-300 text-xs p-1 w-full outline-none"
            value={newName}
            onChange={handleNewItemChange}
            onKeyDown={handleNewItemKeyDown}
            onBlur={handleNewItemBlur}
          />
        </div>
      ) : (
        <div
          className={`flex items-center py-1 px-2 cursor-pointer rounded-sm transition-colors duration-150 ease-in-out ${
            !isFolder && isSelected ? "bg-gray-700" : "hover:bg-gray-700"
          }`}
          style={{ paddingLeft: `${depth * 12}px` }}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
        >
          <div className="w-4 mr-1 flex-shrink-0">
            {isFolder &&
              (isOpen ? (
                <FaChevronDown className="text-gray-400 w-3 h-3" />
              ) : (
                <FaChevronRight className="text-gray-400 w-3 h-3" />
              ))}
          </div>
          <div className="mr-2 flex-shrink-0">
            {isFolder ? (
              isOpen ? (
                <VscFolderOpened className="text-blue-400 w-4 h-4" />
              ) : (
                <VscFolder className="text-blue-400 w-4 h-4" />
              )
            ) : (
              <VscFile className="text-gray-400 w-4 h-4" />
            )}
          </div>
          <span
            className={`text-xs truncate ${
              isFolder ? "text-gray-400" : "text-gray-300"
            }`}
          >
            {node.name}
          </span>
        </div>
      )}
      {isOpen && isFolder && (
        <div>
          {node.children.map((child, index) => (
            <TemplateNode
              key={index}
              node={child}
              depth={depth + 1}
              onContextMenu={onContextMenu}
              initiallyExpanded={initiallyExpanded}
              selectedPath={selectedPath}
              onSelect={onSelect}
              onAddFile={onAddFile}
              onAddFolder={onAddFolder}
              isAddingFile={isAddingFile}
              isAddingFolder={isAddingFolder}
              onCancelAdd={onCancelAdd}
              isRenamingFile={isRenamingFile}
              onRenameFile={onRenameFile}
              onCancelRename={onCancelRename}
              existingPaths={existingPaths}
              readOnly={readOnly}
            />
          ))}
          {(isAddingFile === node.path || isAddingFolder === node.path) &&
            !readOnly && (
              <div
                className="flex items-center py-1 px-2"
                style={{ paddingLeft: `${(depth + 1) * 12}px` }}
              >
                <input
                  ref={newInputRef}
                  type="text"
                  className="bg-gray-800 text-gray-300 text-xs p-1 w-full outline-none"
                  value={newName}
                  onChange={handleNewItemChange}
                  onKeyDown={handleNewItemKeyDown}
                  onBlur={handleNewItemBlur}
                  placeholder={`Enter ${
                    isAddingFile ? "file" : "folder"
                  } name ...`}
                />
              </div>
            )}
        </div>
      )}
    </div>
  );
};

const TemplateTree = () => {
  const { openConfirmation } = useConfirmation();
  const { showToast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [selectedPath, setSelectedPath] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [isAddingFile, setIsAddingFile] = useState(null);
  const [isAddingFolder, setIsAddingFolder] = useState(null);
  const [isRenamingFile, setIsRenamingFile] = useState(null);
  const baseFolders = ["templates/personal", "templates/shared"];
  const initiallyExpanded = true;
  const readOnly = false;

  useEffect(() => {
    fetchTemplates();
    window.addEventListener("saveTemplate", handleSaveTemplate);
    return () => {
      window.removeEventListener("saveTemplate", handleSaveTemplate);
    };
  }, []);

  const fetchTemplates = async () => {
    const personalTemplates = await personalTemplateService.getAllTemplates();
    const sharedTemplates = await sharedTemplateService.getAllTemplates();
    const allTemplates = [
      ...personalTemplates.map((t) => `templates/personal/${t.key}`),
      ...sharedTemplates.map((t) => `templates/shared/${t.key}`),
    ];
    setTemplates(allTemplates);
  };

  const buildTree = (paths) => {
    const root = {
      name: "templates",
      type: "folder",
      path: "templates",
      children: [],
    };
    baseFolders.forEach((folder) => {
      const parts = folder.split("/");
      const child = {
        name: parts[1],
        type: "folder",
        path: folder,
        children: [],
      };
      root.children.push(child);
    });
    paths.forEach((path) => {
      const parts = path.split("/");
      let currentNode = root;
      parts.forEach((part, index) => {
        if (index === 0) return; // Skip the "templates" part
        if (index === parts.length - 1 && part === ".placeholder") return; // Skip placeholder files
        let child = currentNode.children.find((c) => c.name === part);
        if (!child) {
          child = {
            name: part,
            type: index === parts.length - 1 ? "file" : "folder",
            path: parts.slice(0, index + 1).join("/"),
            children: [],
          };
          currentNode.children.push(child);
          currentNode.children.sort((a, b) => a.name.localeCompare(b.name));
        }
        currentNode = child;
      });
    });
    return root;
  };

  const handleDeleteItem = useCallback(
    async (node) => {
      const [, , ...rest] = node.path.split("/");
      const key = rest.join("/");
      const service = getTemplateService(node.path);
      if (node.type === "folder") {
        // Check if the folder contains more than one file (excluding .placeholder)
        const filesInFolder = templates.filter(
          (t) =>
            t.startsWith(node.path) &&
            !t.endsWith("/.placeholder") &&
            t !== node.path
        );

        if (filesInFolder.length > 0) {
          showToast(
            "Cannot delete folder. Please delete all files in the folder first.",
            "error"
          );
          return;
        }
      }
      openConfirmation({
        title: `Delete ${node.type === "folder" ? "Folder" : "Template"}`,
        message: `Are you sure you want to delete ${node.path}?`,
        onConfirm: async () => {
          if (node.type === "folder") {
            // Delete the .placeholder file
            await service.deleteTemplate(`${key}/.placeholder`);
          } else {
            await service.deleteTemplate(key);
          }
          await fetchTemplates();
          setSelectedPath(null);
        },
      });
    },
    [templates, openConfirmation, showToast]
  );

  const handleContextMenu = useCallback(
    (e, node) => {
      e.preventDefault();
      const depth = node.path.split("/").length - 1;
      const menuItems = [];
      if (!readOnly) {
        if (depth === 1) {
          menuItems.push(
            {
              label: "Add file",
              icon: FaPlus,
              onClick: () => setIsAddingFile(node.path),
            },
            {
              label: "Add folder",
              icon: FaFolder,
              onClick: () => setIsAddingFolder(node.path),
            }
          );
        }
        if (depth > 1) {
          if (node.type === "folder") {
            menuItems.push({
              label: "Add file",
              icon: FaPlus,
              onClick: () => setIsAddingFile(node.path),
            });
          }
          menuItems.push(
            {
              label: "Rename",
              icon: FaPencilAlt,
              onClick: () => setIsRenamingFile(node.path),
            }
          );
  
          const canDelete = node.type !== "folder" || 
            !templates.some(t => t.startsWith(node.path) && !t.endsWith("/.placeholder") && t !== node.path);
  
          if (canDelete) {
            menuItems.push({
              label: "Delete",
              icon: FaTrash,
              onClick: () => handleDeleteItem(node),
            });
          } else {
            menuItems.push({
              label: "Delete",
              icon: FaTrash,
              disabled: true,
              onClick: () => showToast("Cannot delete non-empty folder", "error"),
            });
          }
        }
      }
      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        items: menuItems,
      });
    },
    [readOnly, templates, handleDeleteItem, showToast]
  );

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const getTemplateService = useCallback((path) => {
    const [, type] = path.split("/");
    return type === "personal"
      ? personalTemplateService
      : sharedTemplateService;
  }, []);

  const getTemplateFromPath = useCallback((path) => {
    const [, , ...rest] = path.split("/");
    const key = rest.join("/");
    return getTemplateService(path).getTemplate(key);
  }, []);

  const handleSelect = useCallback(
    (path) => {
      setSelectedPath(path);
      getTemplateFromPath(path)
        .then((template) => {
          if (template) {
            window.dispatchEvent(
              new CustomEvent("openTemplate", {
                detail: {
                  path: path,
                  value: template.value,
                },
              })
            );
          } else {
            showToast("Failed to load template", "error");
          }
        })
        .catch((error) => {
          console.error("Error loading template:", error);
          showToast("Failed to load template", "error");
        });
    },
    [getTemplateFromPath, showToast]
  );

  const handleAddTemplate = useCallback(
    async (folderPath, fileName) => {
      const newPath = `${folderPath}/${fileName}`;
      const [, , ...rest] = newPath.split("/");
      const key = rest.join("/");
      const service = getTemplateService(folderPath);

      // Check if the file already exists
      if (templates.includes(newPath)) {
        showToast("A template with that name already exists.", "error");
        return;
      }

      // Create the template
      await service.createTemplate(key, "");
      await fetchTemplates();
      setIsAddingFile(null);
      setSelectedPath(newPath);
    },
    [templates, showToast]
  );

  const handleAddFolder = useCallback(
    async (parentPath, folderName) => {
      const newPath = `${parentPath}/${folderName}`;
      const [, , ...rest] = newPath.split("/");
      const key = rest.join("/");
      const service = getTemplateService(parentPath);

      // Check if the folder already exists
      if (templates.some((t) => t.startsWith(newPath))) {
        showToast("A folder with that name already exists.", "error");
        return;
      }

      // Create a .placeholder file in the new folder
      await service.createTemplate(`${key}/.placeholder`, "");
      await fetchTemplates();
      setIsAddingFolder(null);
    },
    [templates, showToast]
  );

  const handleRenameTemplate = useCallback(async (oldPath, newName) => {
    const [, type, ...oldRest] = oldPath.split("/");
    const oldKey = oldRest.join("/");
    const newKey = [...oldRest.slice(0, -1), newName].join("/");
    const service = getTemplateService(oldPath);
    if (oldKey === newKey) {
      setIsRenamingFile(null);
      return;
    }
    const found = await service.getTemplate(newKey);
    if (found !== null) {
      showToast("A template with that name already exists.", "error");
      setIsRenamingFile(null);
      return;
    }
    const template = await service.getTemplate(oldKey);
    await service.deleteTemplate(oldKey);
    await service.createTemplate(newKey, template.value);
    await fetchTemplates();
    setIsRenamingFile(null);
    setSelectedPath(`templates/${type}/${newKey}`);
  }, []);

  const handleCancelAdd = useCallback(() => {
    setIsAddingFile(null);
    setIsAddingFolder(null);
  }, []);

  const handleCancelRename = useCallback(() => {
    setIsRenamingFile(null);
  }, []);

  const handleSaveTemplate = useCallback(async (e) => {
    const service = getTemplateService(e.detail.path);
    const key = e.detail.path.split("/").slice(2).join("/");
    await service.updateTemplate(key, e.detail.value);
    await fetchTemplates();
  }, []);

  const treeData = buildTree(templates);

  return (
    <>
      <div
        className="overflow-y-auto pt-2 h-[100%] dark-slim-scrollbar"
        onClick={closeContextMenu}
        onContextMenu={(e) => e.preventDefault()}
      >
        <TemplateNode
          node={treeData}
          depth={0}
          onContextMenu={handleContextMenu}
          initiallyExpanded={initiallyExpanded}
          selectedPath={selectedPath}
          onSelect={handleSelect}
          onAddFile={handleAddTemplate}
          onAddFolder={handleAddFolder}
          isAddingFile={isAddingFile}
          isAddingFolder={isAddingFolder}
          onCancelAdd={handleCancelAdd}
          isRenamingFile={isRenamingFile}
          onRenameFile={handleRenameTemplate}
          onCancelRename={handleCancelRename}
          existingPaths={templates}
          readOnly={readOnly}
        />
      </div>
      {contextMenu &&
        contextMenu.items.length > 0 &&
        ReactDOM.createPortal(
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            items={contextMenu.items}
            onClose={closeContextMenu}
          />,
          document.body
        )}
    </>
  );
};

export default TemplateTree;
